/**
 * Pure functions for discount logic - easily testable without React
 */

export interface DiscountState {
   [key: string]: boolean | undefined
}

export interface DiscountConfig {
   DefaultOn?: boolean
   AlwaysApply?: boolean
   DiscountName?: string
   DiscountDescription?: string
}

/**
 * Determines if a discount should be checked based on DefaultOn logic
 */
export function shouldDiscountBeChecked(
   discountValue: boolean | undefined,
   hasDefaultOn: boolean,
   shouldAlwaysApply: boolean,
   isDisabled: boolean,
): boolean {
   return (
      (!isDisabled && shouldAlwaysApply) ||
      (!isDisabled && discountValue === true) ||
      (!isDisabled && hasDefaultOn && discountValue === undefined)
   )
}

/**
 * Determines if a discount needs to be initialized with DefaultOn logic
 */
export function shouldInitializeDefaultOn(
   currentValue: boolean | undefined,
   hasDefaultOn: boolean,
   isDisabled: boolean,
): boolean {
   return !isDisabled && hasDefaultOn && currentValue === undefined
}

/**
 * Creates the updated promotional discounts state when initializing DefaultOn
 */
export function initializeDefaultOnDiscount(
   currentState: DiscountState,
   discountKey: string,
): DiscountState {
   return {
      ...currentState,
      [discountKey]: true,
   }
}

/**
 * Determines if an AlwaysApply discount needs to be initialized
 */
export function shouldInitializeAlwaysApply(
   currentValue: boolean | undefined,
   hasAlwaysApply: boolean,
   isDisabled: boolean,
): boolean {
   return !isDisabled && hasAlwaysApply && currentValue !== true
}

/**
 * Creates the updated promotional discounts state when initializing AlwaysApply
 */
export function initializeAlwaysApplyDiscount(
   currentState: DiscountState,
   discountKey: string,
): DiscountState {
   return {
      ...currentState,
      [discountKey]: true,
   }
}

/**
 * Handles discount toggle logic for DefaultOn discounts
 * Returns the new state or null if no change needed
 */
export function toggleDefaultOnDiscount(
   currentState: DiscountState,
   discountKey: string,
   hasDefaultOn: boolean,
): DiscountState {
   const currentValue = currentState[discountKey]

   if (hasDefaultOn) {
      // For DefaultOn discounts:
      // - If undefined (using default), set to false (explicitly disabled)
      // - If false (explicitly disabled), set to true (explicitly enabled)
      // - If true (explicitly enabled), set to false (explicitly disabled)
      if (currentValue === undefined || currentValue === true) {
         return {
            ...currentState,
            [discountKey]: false,
         }
      } else {
         // currentValue is false, set to true (explicitly enabled)
         return {
            ...currentState,
            [discountKey]: true,
         }
      }
   } else {
      // For regular discounts, toggle normally
      return {
         ...currentState,
         [discountKey]: !currentValue,
      }
   }
}
